import { Schema } from "effect";
import {
  OFFICE_DOCUMENT_HEADER_FOOTER_TEXT_MAX_CHARACTERS,
  OFFICE_DOCUMENT_TABLE_INDEX_MAXIMUM,
  OfficeDocumentAlignmentSchema,
  OfficeDocumentLineSpacingSchema,
  OfficeFontFamilySchema,
  OfficeDocumentFontSizePtSchema,
  OfficeDocumentHighlightSchema,
  OfficeDocumentPageSetupSchema,
  OfficeDocumentTextColorSchema,
  OfficeDocumentVerticalAlignmentSchema,
  OfficePresentationAnimationListSchema,
  OfficePresentationTextAlignmentSchema,
  OfficePresentationTransitionKindSchema,
  isOfficeDocumentTableCellParagraphs,
} from "./office";

export const OfficeEditableStructureBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS = 10_000;
export const OFFICE_EDITABLE_STRUCTURE_MAX_TEXT_CHARACTERS = 10_000_000;
export const OFFICE_EDITABLE_STRUCTURE_MAX_TEXT_FIELD_CHARACTERS =
  OFFICE_EDITABLE_STRUCTURE_MAX_TEXT_CHARACTERS;

const DurableIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(160),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z0-9][A-Za-z0-9._-]*$/u.test(value) ||
      "must be a safe durable identifier without paths or links",
  ),
);

const StyleIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(160),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() &&
        Array.from(value).every((character) => {
          const codePoint = character.codePointAt(0);
          return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
        })) ||
      "must be bounded text without surrounding whitespace or control characters",
  ),
);

const PlainTextSchema = Schema.String.check(
  Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_TEXT_FIELD_CHARACTERS),
);

const ItemCountSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
);

const TextCharacterCountSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(OFFICE_EDITABLE_STRUCTURE_MAX_TEXT_CHARACTERS),
);

export const OfficeEditableDocumentBlockTypeSchema = Schema.Literals([
  "paragraph",
  "heading",
  "list-item",
  "table",
  "image",
  "protected",
]);

export const OfficeEditableDocumentListMetadataSchema = Schema.Struct({
  kind: Schema.Literals(["bullet", "ordered"]),
  level: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 8 })),
});

const DocumentRunOffsetSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(OFFICE_EDITABLE_STRUCTURE_MAX_TEXT_FIELD_CHARACTERS),
);

export const OfficeDocumentFontSlotRoutingSchema = Schema.Struct({
  effectiveFamily: Schema.optionalKey(OfficeFontFamilySchema),
  inheritedFamily: Schema.optionalKey(OfficeFontFamilySchema),
  direct: Schema.Boolean,
}).check(
  Schema.makeFilter(
    (slot) =>
      slot.direct ||
      slot.effectiveFamily === slot.inheritedFamily ||
      "an inherited font slot must expose its inherited family as the effective family",
  ),
);

export const OfficeDocumentFontRoutingSchema = Schema.Struct({
  ascii: OfficeDocumentFontSlotRoutingSchema,
  highAnsi: OfficeDocumentFontSlotRoutingSchema,
  eastAsian: OfficeDocumentFontSlotRoutingSchema,
  complexScript: OfficeDocumentFontSlotRoutingSchema,
  hint: Schema.Literals(["default", "eastAsia", "cs"]),
  forceComplexScript: Schema.Boolean,
  eastAsianLanguage: Schema.Literals(["other", "zhHans", "zhHant"]),
  eastAsianFontCharset: Schema.optionalKey(Schema.Literals(["gb2312", "chinese5"])),
});

export const OfficeEditableDocumentRunSchema = Schema.Struct({
  start: DocumentRunOffsetSchema,
  end: DocumentRunOffsetSchema,
  bold: Schema.Boolean,
  italic: Schema.Boolean,
  underline: Schema.Boolean,
  strikethrough: Schema.Boolean,
  fontSizePt: Schema.optionalKey(OfficeDocumentFontSizePtSchema),
  textColor: Schema.optionalKey(OfficeDocumentTextColorSchema),
  highlight: Schema.optionalKey(OfficeDocumentHighlightSchema),
  verticalAlignment: Schema.optionalKey(OfficeDocumentVerticalAlignmentSchema),
  latinFontFamily: Schema.optionalKey(OfficeFontFamilySchema),
  eastAsianFontFamily: Schema.optionalKey(OfficeFontFamilySchema),
  complexScriptFontFamily: Schema.optionalKey(OfficeFontFamilySchema),
  fontRouting: OfficeDocumentFontRoutingSchema,
  formatEditable: Schema.Boolean,
  fontFamilyEditable: Schema.Boolean,
}).check(
  Schema.makeFilter((run) => run.end > run.start || "document run end must exceed its start"),
);

const DocumentBlockFields = {
  id: DurableIdSchema,
  text: PlainTextSchema,
  styleId: Schema.optionalKey(StyleIdSchema),
  alignment: Schema.optionalKey(OfficeDocumentAlignmentSchema),
  indentLeftTwips: Schema.optionalKey(Schema.Int),
  indentRightTwips: Schema.optionalKey(Schema.Int),
  indentFirstLineTwips: Schema.optionalKey(Schema.Int),
  spaceBeforeTwips: Schema.optionalKey(Schema.Int),
  spaceAfterTwips: Schema.optionalKey(Schema.Int),
  lineSpacing: Schema.optionalKey(OfficeDocumentLineSpacingSchema),
  runs: Schema.optionalKey(
    Schema.Array(OfficeEditableDocumentRunSchema).check(
      Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
    ),
  ),
  textEditable: Schema.optionalKey(Schema.Boolean),
  protected: Schema.Boolean,
} as const;

const DocumentTableIndexSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 0, maximum: OFFICE_DOCUMENT_TABLE_INDEX_MAXIMUM }),
);
const DocumentTableSpanSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS }),
);
const DocumentTableColumnWidthSchema = Schema.Finite.check(
  Schema.isGreaterThan(0),
  Schema.isLessThanOrEqualTo(100),
);
const OfficeEditableDocumentTableGridGapSchema = Schema.Struct({
  kind: Schema.Literal("grid-gap"),
  columnSpan: DocumentTableSpanSchema,
});
export const OfficeEditableDocumentTableCellSchema = Schema.Struct({
  kind: Schema.Literal("cell"),
  cellIndex: DocumentTableIndexSchema,
  paragraphs: Schema.Array(PlainTextSchema).check(
    Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
  columnSpan: DocumentTableSpanSchema,
  rowSpan: DocumentTableSpanSchema,
  verticalMerge: Schema.Literals(["none", "restart", "continue"]),
  textEditable: Schema.Boolean,
  fill: Schema.optionalKey(OfficeDocumentTextColorSchema),
  textColor: Schema.optionalKey(OfficeDocumentTextColorSchema),
  bold: Schema.Boolean,
  alignment: OfficeDocumentAlignmentSchema,
  verticalAlignment: Schema.Literals(["top", "center", "bottom"]),
  textDirection: Schema.optionalKey(Schema.Literals(["tbRl", "btLr"])),
}).check(
  Schema.makeFilter(
    (cell) =>
      cell.verticalMerge !== "continue" ||
      (!cell.textEditable && cell.rowSpan === 1) ||
      "vertical-merge continuation cells must be read-only and cannot span rows",
  ),
  Schema.makeFilter(
    (cell) =>
      !cell.textEditable ||
      isOfficeDocumentTableCellParagraphs(cell.paragraphs) ||
      "editable table cells must fit the canonical native paragraph boundary",
  ),
);
export const OfficeEditableDocumentTableRowSchema = Schema.Struct({
  rowIndex: DocumentTableIndexSchema,
  cells: Schema.Array(
    Schema.Union([OfficeEditableDocumentTableGridGapSchema, OfficeEditableDocumentTableCellSchema]),
  ).check(Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS)),
}).check(
  Schema.makeFilter((row) => {
    let physicalCellIndex = 0;
    let gridColumnCount = 0;
    for (const cell of row.cells) {
      gridColumnCount += cell.columnSpan;
      if (gridColumnCount > OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS) {
        return "document table row grid width exceeds the bounded column limit";
      }
      if (cell.kind === "grid-gap") continue;
      if (cell.cellIndex !== physicalCellIndex) {
        return "document table cellIndex must match physical cell order after grid gaps";
      }
      physicalCellIndex += 1;
    }
    return true;
  }),
);
export const OfficeEditableDocumentTableSchema = Schema.Struct({
  columnWidthsPct: Schema.optionalKey(
    Schema.Array(DocumentTableColumnWidthSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(256),
    ),
  ),
  rows: Schema.Array(OfficeEditableDocumentTableRowSchema).check(
    Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
}).check(
  Schema.makeFilter(
    (table) =>
      table.rows.every((row, rowIndex) => row.rowIndex === rowIndex) ||
      "document table rowIndex must match physical row order",
  ),
);

export function officeEditableDocumentTableText(
  table: typeof OfficeEditableDocumentTableSchema.Type,
): string {
  return table.rows
    .map((row) =>
      row.cells
        .map((cell) => (cell.kind === "grid-gap" ? "" : cell.paragraphs.join("\n")))
        .join("\t"),
    )
    .filter((row) => row !== "")
    .join("\n");
}

export const OfficeEditableDocumentBlockSchema = Schema.Union([
  Schema.Struct({
    ...DocumentBlockFields,
    type: Schema.Literals(["paragraph", "image", "protected"]),
  }),
  Schema.Struct({
    ...DocumentBlockFields,
    type: Schema.Literal("table"),
    table: OfficeEditableDocumentTableSchema,
  }),
  Schema.Struct({
    ...DocumentBlockFields,
    type: Schema.Literal("heading"),
    headingLevel: Schema.optionalKey(
      Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 9 })),
    ),
  }),
  Schema.Struct({
    ...DocumentBlockFields,
    type: Schema.Literal("list-item"),
    list: Schema.optionalKey(OfficeEditableDocumentListMetadataSchema),
  }),
]).check(
  Schema.makeFilter(
    (block) =>
      (block.type !== "image" && block.type !== "protected") ||
      block.protected ||
      "image and protected blocks must be marked protected",
  ),
  Schema.makeFilter((block) => {
    if (block.runs === undefined) return true;
    if (block.text.length === 0) {
      return block.runs.length === 0 || "empty document blocks cannot contain run spans";
    }
    let cursor = 0;
    for (const run of block.runs) {
      if (run.start !== cursor) return "document run spans must be ordered and contiguous";
      cursor = run.end;
    }
    return cursor === block.text.length || "document run spans must cover the projected text";
  }),
  Schema.makeFilter((block) => {
    const paragraphLike =
      block.type === "paragraph" || block.type === "heading" || block.type === "list-item";
    if (
      !paragraphLike &&
      (block.alignment !== undefined ||
        block.runs !== undefined ||
        block.indentLeftTwips !== undefined ||
        block.indentRightTwips !== undefined ||
        block.indentFirstLineTwips !== undefined ||
        block.spaceBeforeTwips !== undefined ||
        block.spaceAfterTwips !== undefined ||
        block.lineSpacing !== undefined)
    ) {
      return "only paragraph-like document blocks can expose alignment, geometry, or run spans";
    }
    if (block.textEditable !== true) return true;
    if (!paragraphLike || block.protected) {
      return "editable document text must belong to an unprotected paragraph-like block";
    }
    if (block.runs === undefined || block.runs.some((run) => !run.formatEditable)) {
      return "editable document text requires a complete editable run projection";
    }
    return true;
  }),
  Schema.makeFilter((block) => {
    if (block.type !== "table") return true;
    if (block.textEditable === true) {
      return "table blocks cannot expose flattened text as editable";
    }
    if (block.text !== officeEditableDocumentTableText(block.table)) {
      return "document table text must equal its canonical flattened cell projection";
    }
    if (
      block.protected &&
      block.table.rows.some((row) =>
        row.cells.some((cell) => cell.kind === "cell" && cell.textEditable),
      )
    ) {
      return "protected document tables cannot expose editable cells";
    }
    return true;
  }),
);

const OfficeEditableDocumentHeaderFooterTextSchema = Schema.String.check(
  Schema.isMaxLength(OFFICE_DOCUMENT_HEADER_FOOTER_TEXT_MAX_CHARACTERS),
);

export const OfficeEditableDocumentLayoutSchema = Schema.Struct({
  pageSetup: OfficeDocumentPageSetupSchema,
  sectionCount: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 1_000 })),
  pageSetupEditable: Schema.Boolean,
  headerFooter: Schema.Struct({
    headerText: OfficeEditableDocumentHeaderFooterTextSchema,
    footerText: OfficeEditableDocumentHeaderFooterTextSchema,
    headerHasPageNumber: Schema.Boolean,
    footerHasPageNumber: Schema.Boolean,
    differentFirstPage: Schema.Boolean,
    differentOddEven: Schema.Boolean,
    headerEditable: Schema.Boolean,
    footerEditable: Schema.Boolean,
  }),
}).check(
  Schema.makeFilter(
    (layout) =>
      layout.sectionCount === 1 ||
      (!layout.pageSetupEditable &&
        !layout.headerFooter.headerEditable &&
        !layout.headerFooter.footerEditable) ||
      "multi-section document layout must be projected read-only",
  ),
  Schema.makeFilter(
    (layout) =>
      (!layout.headerFooter.differentFirstPage && !layout.headerFooter.differentOddEven) ||
      (!layout.headerFooter.headerEditable && !layout.headerFooter.footerEditable) ||
      "variant headers and footers must be projected read-only",
  ),
);

export const OfficeEditableDocumentCommentSchema = Schema.Struct({
  id: DurableIdSchema,
  author: PlainTextSchema,
  text: PlainTextSchema,
  initials: Schema.optionalKey(PlainTextSchema),
  date: Schema.optionalKey(PlainTextSchema),
  parentId: Schema.optionalKey(DurableIdSchema),
  resolved: Schema.Boolean,
  anchorBlockIds: Schema.Array(DurableIdSchema),
});

export const OfficeEditableDocumentStructureSchema = Schema.Struct({
  kind: Schema.Literal("document"),
  layout: OfficeEditableDocumentLayoutSchema,
  comments: Schema.Array(OfficeEditableDocumentCommentSchema).check(
    Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
  blocks: Schema.Array(OfficeEditableDocumentBlockSchema).check(
    Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
  itemCount: ItemCountSchema,
  textCharacterCount: TextCharacterCountSchema,
  truncated: Schema.Literal(false),
}).check(
  Schema.makeFilter((structure) => {
    if (new Set(structure.blocks.map(({ id }) => id)).size !== structure.blocks.length) {
      return "document block ids must be unique";
    }
    const commentIds = new Set<string>();
    const blockIds = new Set(structure.blocks.map(({ id }) => id));
    for (const comment of structure.comments) {
      if (commentIds.has(comment.id)) return "document comment ids must be unique";
      commentIds.add(comment.id);
    }
    for (const comment of structure.comments) {
      if (comment.parentId === comment.id) return "document comments cannot parent themselves";
      if (comment.parentId !== undefined && !commentIds.has(comment.parentId)) {
        return "document comment parents must exist";
      }
      if (
        new Set(comment.anchorBlockIds).size !== comment.anchorBlockIds.length ||
        comment.anchorBlockIds.some((blockId) => !blockIds.has(blockId))
      ) {
        return "document comment anchors must be unique projected block ids";
      }
    }
    const itemCount =
      structure.comments.length +
      structure.blocks.reduce((total, block) => {
        if (block.type !== "table") return total + 1;
        return (
          total +
          1 +
          block.table.rows.reduce(
            (tableTotal, row) =>
              tableTotal +
              1 +
              row.cells.reduce(
                (rowTotal, cell) =>
                  rowTotal + 1 + (cell.kind === "cell" ? cell.paragraphs.length : 0),
                0,
              ),
            0,
          )
        );
      }, 0);
    if (structure.itemCount !== itemCount) {
      return "document itemCount must equal the comment, recursive block, and table item count";
    }
    const runCount = structure.blocks.reduce(
      (total, block) => total + (block.runs?.length ?? 0),
      0,
    );
    if (runCount > OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS) {
      return "document run span count exceeds the bounded item limit";
    }
    const textCharacterCount =
      structure.comments.reduce(
        (total, comment) =>
          total +
          comment.author.length +
          comment.text.length +
          (comment.initials?.length ?? 0) +
          (comment.date?.length ?? 0),
        0,
      ) +
      structure.blocks.reduce(
        (total, block) =>
          total +
          block.text.length +
          (block.type === "table"
            ? block.table.rows.reduce(
                (tableTotal, row) =>
                  tableTotal +
                  row.cells.reduce(
                    (rowTotal, cell) =>
                      rowTotal +
                      (cell.kind === "cell"
                        ? cell.paragraphs.reduce(
                            (paragraphTotal, paragraph) => paragraphTotal + paragraph.length,
                            0,
                          )
                        : 0),
                    0,
                  ),
                0,
              )
            : 0),
        0,
      ) +
      structure.layout.headerFooter.headerText.length +
      structure.layout.headerFooter.footerText.length;
    return (
      textCharacterCount === structure.textCharacterCount ||
      "document textCharacterCount must equal the projected text length"
    );
  }),
);

export const OfficeEditablePresentationElementTypeSchema = Schema.Literals([
  "text",
  "shape",
  "picture",
  "group",
  "table",
  "chart",
  "smart-art",
  "embedded-object",
  "connector",
  "media",
  "unsupported",
]);

const EmuCoordinateSchema = Schema.Finite.check(
  Schema.isGreaterThanOrEqualTo(-100_000_000),
  Schema.isLessThanOrEqualTo(100_000_000),
);

const EmuExtentSchema = Schema.Finite.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(100_000_000),
);

const RotationDegreesSchema = Schema.Finite.check(
  Schema.isGreaterThanOrEqualTo(-2_147_483_648 / 60_000),
  Schema.isLessThanOrEqualTo(2_147_483_647 / 60_000),
);

export const OfficeEditablePresentationTransformSchema = Schema.Struct({
  coordinateSpace: Schema.Literals(["slide", "parent-element"]),
  xEmu: EmuCoordinateSchema,
  yEmu: EmuCoordinateSchema,
  widthEmu: EmuExtentSchema,
  heightEmu: EmuExtentSchema,
  rotationDegrees: RotationDegreesSchema,
  flipHorizontal: Schema.Boolean,
  flipVertical: Schema.Boolean,
});

const OfficeEditablePresentationBooleanFormatStateSchema = Schema.Union([
  Schema.Boolean,
  Schema.Literal("mixed"),
]);
const OfficeEditablePresentationFontSizeStateSchema = Schema.Union([
  Schema.Finite.check(Schema.isBetween({ minimum: 1, maximum: 4_000 })),
  Schema.Literals(["mixed", "inherited"]),
]);
export const OfficeEditablePresentationFontFamilyStateSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("editable"), family: OfficeFontFamilySchema }),
  Schema.Struct({ kind: Schema.Literal("mixed") }),
  Schema.Struct({ kind: Schema.Literal("inherited") }),
  Schema.Struct({ kind: Schema.Literal("theme") }),
  Schema.Struct({ kind: Schema.Literal("script-specific"), family: OfficeFontFamilySchema }),
]);
export const OfficeEditablePresentationTextFormatSchema = Schema.Struct({
  bold: OfficeEditablePresentationBooleanFormatStateSchema,
  italic: OfficeEditablePresentationBooleanFormatStateSchema,
  underline: OfficeEditablePresentationBooleanFormatStateSchema,
  fontSizePt: OfficeEditablePresentationFontSizeStateSchema,
  fontFamily: OfficeEditablePresentationFontFamilyStateSchema,
  alignment: Schema.Union([OfficePresentationTextAlignmentSchema, Schema.Literal("mixed")]),
});

export const OfficeEditablePresentationElementSchema = Schema.Struct({
  id: DurableIdSchema,
  parentElementId: Schema.optionalKey(DurableIdSchema),
  type: OfficeEditablePresentationElementTypeSchema,
  textSummary: PlainTextSchema,
  paragraphCount: ItemCountSchema,
  textFormat: Schema.optionalKey(OfficeEditablePresentationTextFormatSchema),
  transform: OfficeEditablePresentationTransformSchema,
  editable: Schema.Boolean,
}).check(
  Schema.makeFilter(
    (element) =>
      element.textFormat === undefined ||
      element.type === "text" ||
      element.type === "shape" ||
      "only presentation text and shape elements can expose text formatting",
  ),
  Schema.makeFilter(
    (element) =>
      element.type === "text" ||
      element.type === "shape" ||
      element.paragraphCount === 0 ||
      "only presentation text and shape elements can expose paragraph counts",
  ),
);

export const OfficeEditablePresentationSlideSchema = Schema.Struct({
  id: DurableIdSchema,
  index: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS - 1),
  ),
  hidden: Schema.Boolean,
  notes: PlainTextSchema,
  transition: OfficePresentationTransitionKindSchema,
  animations: OfficePresentationAnimationListSchema,
  animationEditability: Schema.Literals(["editable", "read-only"]),
  unsupportedAnimationCount: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
  elements: Schema.Array(OfficeEditablePresentationElementSchema).check(
    Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
});

type EditablePresentationSlideValue = typeof OfficeEditablePresentationSlideSchema.Type;
type EditablePresentationElementValue = EditablePresentationSlideValue["elements"][number];

function hasNonEditablePresentationAnimation(
  slide: EditablePresentationSlideValue,
  elements: ReadonlyMap<string, EditablePresentationElementValue>,
): boolean {
  if (slide.unsupportedAnimationCount > 0) return true;
  return slide.animations.some((animation) => {
    const target = elements.get(animation.elementId);
    return (
      target === undefined ||
      target.parentElementId !== undefined ||
      !target.editable ||
      (animation.paragraph !== undefined &&
        ((target.type !== "text" && target.type !== "shape") ||
          animation.paragraph >= target.paragraphCount))
    );
  });
}

const NonEditablePresentationElementTypes: ReadonlySet<string> = new Set([
  "smart-art",
  "embedded-object",
  "connector",
  "media",
  "unsupported",
]);

function presentationElementIssue(
  element: EditablePresentationElementValue,
  priorElements: ReadonlyMap<string, EditablePresentationElementValue>,
): string | undefined {
  if (priorElements.has(element.id)) return "presentation element ids must be unique per slide";
  if (element.parentElementId === undefined) {
    if (element.transform.coordinateSpace !== "slide") {
      return "top-level presentation elements must use slide coordinates";
    }
  } else {
    const parent = priorElements.get(element.parentElementId);
    if (parent?.type !== "group") {
      return "nested presentation elements must reference a preceding group parent";
    }
    if (element.transform.coordinateSpace !== "parent-element") {
      return "nested presentation elements must use parent-element coordinates";
    }
    if (element.editable) return "nested presentation elements must not be editable";
  }
  if (NonEditablePresentationElementTypes.has(element.type) && element.editable) {
    return "protected presentation element types must not be editable";
  }
  return undefined;
}

function presentationSlideProjectionSize(
  slide: EditablePresentationSlideValue,
): { readonly itemCount: number; readonly textCharacterCount: number } | string {
  const priorElements = new Map<string, EditablePresentationElementValue>();
  let textCharacterCount = slide.notes.length;
  for (const element of slide.elements) {
    const issue = presentationElementIssue(element, priorElements);
    if (issue !== undefined) return issue;
    priorElements.set(element.id, element);
    textCharacterCount += element.textSummary.length;
  }
  for (const animation of slide.animations) {
    if (!priorElements.has(animation.elementId)) {
      return "presentation animations must target projected elements on the same slide";
    }
  }
  if (
    slide.animationEditability === "editable" &&
    hasNonEditablePresentationAnimation(slide, priorElements)
  ) {
    return "editable presentation animations must target editable top-level elements with valid paragraph scope";
  }
  return {
    itemCount:
      1 + slide.elements.length + slide.animations.length + slide.unsupportedAnimationCount,
    textCharacterCount,
  };
}

export const OfficeEditablePresentationStructureSchema = Schema.Struct({
  kind: Schema.Literal("presentation"),
  slides: Schema.Array(OfficeEditablePresentationSlideSchema).check(
    Schema.isMaxLength(OFFICE_EDITABLE_STRUCTURE_MAX_ITEMS),
  ),
  itemCount: ItemCountSchema,
  textCharacterCount: TextCharacterCountSchema,
  truncated: Schema.Literal(false),
}).check(
  Schema.makeFilter((structure) => {
    const slideIds = new Set<string>();
    let itemCount = 0;
    let textCharacterCount = 0;

    for (let slideIndex = 0; slideIndex < structure.slides.length; slideIndex += 1) {
      const slide = structure.slides[slideIndex];
      if (!slide) continue;
      if (slideIds.has(slide.id)) return "presentation slide ids must be unique";
      slideIds.add(slide.id);
      if (slide.index !== slideIndex) return "presentation slide indices must match their order";
      const projectionSize = presentationSlideProjectionSize(slide);
      if (typeof projectionSize === "string") return projectionSize;
      itemCount += projectionSize.itemCount;
      textCharacterCount += projectionSize.textCharacterCount;
    }

    if (itemCount !== structure.itemCount) {
      return "presentation itemCount must equal the projected slides, elements, and timing items";
    }
    return (
      textCharacterCount === structure.textCharacterCount ||
      "presentation textCharacterCount must equal the projected text length"
    );
  }),
);

export const OfficeEditableStructureSchema = Schema.Union([
  OfficeEditableDocumentStructureSchema,
  OfficeEditablePresentationStructureSchema,
]);

export type OfficeEditableDocumentBlockType = typeof OfficeEditableDocumentBlockTypeSchema.Type;
export type OfficeEditableDocumentBlockTypeEncoded =
  typeof OfficeEditableDocumentBlockTypeSchema.Encoded;
export type OfficeEditableDocumentListMetadata =
  typeof OfficeEditableDocumentListMetadataSchema.Type;
export type OfficeEditableDocumentListMetadataEncoded =
  typeof OfficeEditableDocumentListMetadataSchema.Encoded;
export type OfficeDocumentFontSlotRouting = typeof OfficeDocumentFontSlotRoutingSchema.Type;
export type OfficeDocumentFontSlotRoutingEncoded =
  typeof OfficeDocumentFontSlotRoutingSchema.Encoded;
export type OfficeDocumentFontRouting = typeof OfficeDocumentFontRoutingSchema.Type;
export type OfficeDocumentFontRoutingEncoded = typeof OfficeDocumentFontRoutingSchema.Encoded;
export type OfficeEditableDocumentRun = typeof OfficeEditableDocumentRunSchema.Type;
export type OfficeEditableDocumentRunEncoded = typeof OfficeEditableDocumentRunSchema.Encoded;
export type OfficeEditableDocumentTableCell = typeof OfficeEditableDocumentTableCellSchema.Type;
export type OfficeEditableDocumentTableCellEncoded =
  typeof OfficeEditableDocumentTableCellSchema.Encoded;
export type OfficeEditableDocumentTableRow = typeof OfficeEditableDocumentTableRowSchema.Type;
export type OfficeEditableDocumentTableRowEncoded =
  typeof OfficeEditableDocumentTableRowSchema.Encoded;
export type OfficeEditableDocumentTable = typeof OfficeEditableDocumentTableSchema.Type;
export type OfficeEditableDocumentTableEncoded = typeof OfficeEditableDocumentTableSchema.Encoded;
export type OfficeEditableDocumentBlock = typeof OfficeEditableDocumentBlockSchema.Type;
export type OfficeEditableDocumentBlockEncoded = typeof OfficeEditableDocumentBlockSchema.Encoded;
export type OfficeEditableDocumentLayout = typeof OfficeEditableDocumentLayoutSchema.Type;
export type OfficeEditableDocumentLayoutEncoded = typeof OfficeEditableDocumentLayoutSchema.Encoded;
export type OfficeEditableDocumentComment = typeof OfficeEditableDocumentCommentSchema.Type;
export type OfficeEditableDocumentCommentEncoded =
  typeof OfficeEditableDocumentCommentSchema.Encoded;
export type OfficeEditableDocumentStructure = typeof OfficeEditableDocumentStructureSchema.Type;
export type OfficeEditableDocumentStructureEncoded =
  typeof OfficeEditableDocumentStructureSchema.Encoded;
export type OfficeEditablePresentationElementType =
  typeof OfficeEditablePresentationElementTypeSchema.Type;
export type OfficeEditablePresentationElementTypeEncoded =
  typeof OfficeEditablePresentationElementTypeSchema.Encoded;
export type OfficeEditablePresentationTransform =
  typeof OfficeEditablePresentationTransformSchema.Type;
export type OfficeEditablePresentationTransformEncoded =
  typeof OfficeEditablePresentationTransformSchema.Encoded;
export type OfficeEditablePresentationTextFormat =
  typeof OfficeEditablePresentationTextFormatSchema.Type;
export type OfficeEditablePresentationTextFormatEncoded =
  typeof OfficeEditablePresentationTextFormatSchema.Encoded;
export type OfficeEditablePresentationFontFamilyState =
  typeof OfficeEditablePresentationFontFamilyStateSchema.Type;
export type OfficeEditablePresentationFontFamilyStateEncoded =
  typeof OfficeEditablePresentationFontFamilyStateSchema.Encoded;
export type OfficeEditablePresentationElement = typeof OfficeEditablePresentationElementSchema.Type;
export type OfficeEditablePresentationElementEncoded =
  typeof OfficeEditablePresentationElementSchema.Encoded;
export type OfficeEditablePresentationSlide = typeof OfficeEditablePresentationSlideSchema.Type;
export type OfficeEditablePresentationSlideEncoded =
  typeof OfficeEditablePresentationSlideSchema.Encoded;
export type OfficeEditablePresentationStructure =
  typeof OfficeEditablePresentationStructureSchema.Type;
export type OfficeEditablePresentationStructureEncoded =
  typeof OfficeEditablePresentationStructureSchema.Encoded;
export type OfficeEditableStructure = typeof OfficeEditableStructureSchema.Type;
export type OfficeEditableStructureEncoded = typeof OfficeEditableStructureSchema.Encoded;

export const decodeOfficeEditableStructure = Schema.decodeUnknownSync(
  OfficeEditableStructureSchema,
  OfficeEditableStructureBoundaryParseOptions,
);

export const decodeOfficeEditableDocumentStructure = Schema.decodeUnknownSync(
  OfficeEditableDocumentStructureSchema,
  OfficeEditableStructureBoundaryParseOptions,
);

export const decodeOfficeEditableDocumentComment = Schema.decodeUnknownSync(
  OfficeEditableDocumentCommentSchema,
  OfficeEditableStructureBoundaryParseOptions,
);

export const encodeOfficeEditableStructure = Schema.encodeSync(
  OfficeEditableStructureSchema,
  OfficeEditableStructureBoundaryParseOptions,
);
